import { ButtonVariant, CoButton } from "components/CoButton";
import { CreateNewBookmarkListModal, useBookmarks } from "features/Bookmarks";
import { IBookmarkListItem } from "helpers/bookmark";
import useLabels from "hooks/useLabels";
import { useCallback, useState } from "react";
import BookmarkListItemWithActions from "./components/BookmarkListItemWithActions";
import ConfirmDeleteListModal from "./components/ConfirmDeleteListModal";
import EditListModal from "./components/EditListModal";

interface BookmarkListsProps {
  hideHeader?: boolean;
}

export const BookmarkLists = ({ hideHeader = false }: BookmarkListsProps) => {
  const [labels] = useLabels("features.bookmark-lists");

  const {
    bookmarkLists,
    isBookmarkListLoaded,
    isBookmarkItemsLoaded,
    deleteBookmarkList,
  } = useBookmarks();

  const [currentList, setCurrentList] = useState<IBookmarkListItem | undefined>(
    undefined
  );
  const [
    isCreateNewBookmarkListModalOpen,
    setIsCreateNewBookmarkListModalOpen,
  ] = useState(false);
  const [isEditListModalOpen, setIsEditListModalOpen] = useState(false);
  const [isConfirmDeleteListModalOpen, setIsConfirmDeleteListModalOpen] =
    useState(false);

  const handleClickCreateNewList = async () => {
    setIsCreateNewBookmarkListModalOpen(true);
  };

  const handleClickCancelCreateNewList = async () => {
    setIsCreateNewBookmarkListModalOpen(false);
  };

  const handleClickEditList = async (list: IBookmarkListItem) => {
    setCurrentList(list);
    setIsEditListModalOpen(true);
  };

  const hideEditListModal = () => {
    setIsEditListModalOpen(false);
  };

  const handleClickDeleteList = async (list: IBookmarkListItem) => {
    setCurrentList(list);
    setIsConfirmDeleteListModalOpen(true);
  };

  const hideDeleteListModal = () => {
    setIsConfirmDeleteListModalOpen(false);
  };

  const handleConfirmDeleteList = useCallback(async () => {
    setIsConfirmDeleteListModalOpen(false);

    if (
      currentList !== undefined &&
      isBookmarkListLoaded &&
      isBookmarkItemsLoaded
    ) {
      await deleteBookmarkList(currentList.id);
      setCurrentList(undefined);
    }
  }, [
    currentList,
    isBookmarkListLoaded,
    isBookmarkItemsLoaded,
    deleteBookmarkList,
  ]);

  return (
    <div
      data-testid="bookmark-lists"
      className="flex flex-col gap-6 px-4 my-6 sm:px-6 md:px-0"
    >
      {!hideHeader && (
        <div className="flex justify-between">
          <h1 className="h1-base">{labels["page-title"]}</h1>

          <CoButton
            variant={ButtonVariant.Tertiary}
            label={labels["create-new"]}
            icon="icon-plus"
            onClick={handleClickCreateNewList}
          />
        </div>
      )}

      <ul className="flex flex-col gap-3">
        {bookmarkLists.map((bookmarkList: IBookmarkListItem) => (
          <li key={bookmarkList.id}>
            <BookmarkListItemWithActions
              bookmarkList={bookmarkList}
              onClickEditList={() => {
                handleClickEditList(bookmarkList);
              }}
              onClickDeleteList={() => {
                handleClickDeleteList(bookmarkList);
              }}
            />
          </li>
        ))}
      </ul>

      <EditListModal
        open={isEditListModalOpen}
        onClose={hideEditListModal}
        bookmarkList={currentList!}
      />

      <CreateNewBookmarkListModal
        open={isCreateNewBookmarkListModalOpen}
        onClose={handleClickCancelCreateNewList}
      />

      <ConfirmDeleteListModal
        open={isConfirmDeleteListModalOpen}
        onConfirm={handleConfirmDeleteList}
        onClose={hideDeleteListModal}
      />
    </div>
  );
};
